/**
 * Bank Entity Service - Extended Risk Evaluation (Phase 2)
 * Provides mock implementations for advanced risk modules.
 */

import {
  type HouseholdCreditInput,
  type HouseholdCreditAnalysisResult,
  type CollateralValuationRequest,
  type CollateralValuationResult,
  type CollateralAssetValuation,
  type CollateralValuationSummary,
  type CommunityRiskInput,
  type CommunityRiskProfileResult,
  type PortfolioRiskInput,
  type PortfolioRiskAggregation,
  type ProjectViabilityInput,
  type ProjectViabilityResult,
  type AdvancedProposalRiskBundle,
  type CollateralStressScenarioDefinition,
  type CollateralMultiScenarioResult,
  CollateralStressScenarioKey,
} from '@/types/entities/bank/riskExtensions';

import type { CollateralAsset, LoanRiskAssessment } from '@/types/entities/bank';

class BankRiskEvaluationService {
  // ===== Household Credit =====
  analyzeHouseholdCredit(inputs: HouseholdCreditInput[]): HouseholdCreditAnalysisResult[] {
    return inputs.map((i) => {
      const dtiRatio = i.monthlyDebtPayments / Math.max(i.monthlyIncome, 1);
      const paymentHistoryScore = Math.max(0, 100 - i.latePaymentsLast24M * 5);
      const stabilityScore = 70; // placeholder heuristic
      const utilizationRatio = 0.35; // placeholder
      const compositeCreditScore = Math.round(
        paymentHistoryScore * 0.4 + stabilityScore * 0.2 + (1 - dtiRatio) * 100 * 0.4,
      );
      const riskLevel: LoanRiskAssessment['overallRisk'] =
        compositeCreditScore > 80 ? 'low' : compositeCreditScore > 60 ? 'medium' : 'high';
      const flags: string[] = [];
      if (dtiRatio > 0.45) flags.push('High DTI');
      if ((i.creditScore || 0) < 620) flags.push('Low FICO');
      return {
        householdId: i.householdId,
        residentId: i.residentId,
        inputs: i,
        dtiRatio,
        utilizationRatio,
        paymentHistoryScore,
        stabilityScore,
        riskLevel,
        compositeCreditScore,
        flags,
        recommendations: flags.length ? ['Consider additional collateral or conditions'] : [],
      };
    });
  }

  // ===== Collateral Valuation =====
  valuateCollateral(request: CollateralValuationRequest): CollateralValuationResult {
    const valuations: CollateralAssetValuation[] = request.assets.map((a) => {
      const haircut =
        request.stressScenario === 'severe_decline'
          ? 0.35
          : request.stressScenario === 'mild_decline'
            ? 0.15
            : 0.1;
      const stressedValue = a.currentValue * (1 - haircut);
      return {
        assetId: a.id,
        currentValue: a.currentValue,
        stressedValue,
        haircutPercent: haircut * 100,
        liquidityDays: Math.max(5, 60 - a.liquidityScore),
        confidence: 'medium',
      };
    });
    const summary: CollateralValuationSummary = this.buildCollateralSummary(valuations, request);
    return { ...summary, valuations, scenario: request };
  }

  runCollateralStressMatrix(
    request: CollateralValuationRequest,
    scenarios?: CollateralStressScenarioDefinition[],
  ): CollateralMultiScenarioResult {
    const defaultScenarios: CollateralStressScenarioDefinition[] = scenarios || [
      { key: 'base', label: 'Base' },
      { key: 'mild_decline', label: 'Mild Decline', haircutOverride: 0.1 },
      { key: 'moderate_decline', label: 'Moderate Decline', haircutOverride: 0.2 },
      { key: 'severe_decline', label: 'Severe Decline', haircutOverride: 0.35 },
      {
        key: 'extreme_crisis',
        label: 'Extreme Crisis',
        haircutOverride: 0.5,
        additionalLiquidityDays: 25,
      },
    ];
    const results = {} as Record<CollateralStressScenarioKey, CollateralValuationResult>;
    for (const sc of defaultScenarios) {
      const variant: CollateralValuationRequest = { ...request, stressScenario: 'base' };
      const valuations = request.assets.map((a) => {
        const baseHaircut = sc.haircutOverride ?? 0.1;
        const stressedValue = a.currentValue * (1 - baseHaircut);
        return {
          assetId: a.id,
          currentValue: a.currentValue,
          stressedValue,
          haircutPercent: baseHaircut * 100,
          liquidityDays: Math.max(5, 60 - a.liquidityScore) + (sc.additionalLiquidityDays || 0),
          confidence: 'medium',
        } as CollateralAssetValuation;
      });
      const summary = this.buildCollateralSummary(valuations, variant);
      results[sc.key] = { ...summary, valuations, scenario: variant };
    }
    return { scenarios: defaultScenarios, results };
  }

  private buildCollateralSummary(
    vals: CollateralAssetValuation[],
    request: CollateralValuationRequest,
  ): CollateralValuationSummary {
    const totalCurrentValue = vals.reduce((s, v) => s + v.currentValue, 0);
    const totalStressedValue = vals.reduce((s, v) => s + v.stressedValue, 0);
    const averageHaircut = vals.length
      ? vals.reduce((s, v) => s + v.haircutPercent, 0) / vals.length
      : 0;
    const weightedLiquidityDays = vals.length
      ? Math.round(vals.reduce((s, v) => s + v.liquidityDays, 0) / vals.length)
      : 0;
    const coverageRatioBase = totalCurrentValue / Math.max(totalStressedValue, 1); // placeholder
    const coverageRatioStressed = totalStressedValue / Math.max(totalCurrentValue, 1);
    const concentrationFlags: string[] = [];
    if (vals.filter((v) => v.liquidityDays > 45).length / Math.max(vals.length, 1) > 0.4)
      concentrationFlags.push('High slow-liquidity asset share');
    return {
      totalCurrentValue,
      totalStressedValue,
      averageHaircut,
      weightedLiquidityDays,
      coverageRatioBase,
      coverageRatioStressed,
      concentrationFlags,
      recommendations: concentrationFlags.length ? ['Encourage more liquid asset pledges'] : [],
    };
  }

  // ===== Community Risk =====
  profileCommunity(input: CommunityRiskInput): CommunityRiskProfileResult {
    const weights = {
      geographic: 0.15,
      economic: 0.2,
      delinquency: 0.2,
      governance: 0.15,
      maintenance: 0.15,
      volatility: 0.15,
    };
    const delinquencyScore = Math.max(0, 100 - input.historicalDelinquencyRate * 2);
    const volatilityScore = Math.max(0, 100 - input.participationVolatility * 5);
    const compositeScore =
      input.geographicRiskScore * weights.geographic +
      input.economicDiversityScore * weights.economic +
      delinquencyScore * weights.delinquency +
      input.governanceStabilityScore * weights.governance +
      input.maintenanceQualityScore * weights.maintenance +
      volatilityScore * weights.volatility;
    const riskLevel: LoanRiskAssessment['overallRisk'] =
      compositeScore > 75 ? 'low' : compositeScore > 55 ? 'medium' : 'high';
    return {
      communityId: input.communityId,
      compositeScore,
      riskLevel,
      factorBreakdown: {
        geographic: input.geographicRiskScore,
        economic: input.economicDiversityScore,
        delinquency: delinquencyScore,
        governance: input.governanceStabilityScore,
        maintenance: input.maintenanceQualityScore,
        participation: volatilityScore,
      },
      strengths: compositeScore > 70 ? ['Strong composite profile'] : [],
      weaknesses: compositeScore < 60 ? ['Elevated delinquency or volatility risk'] : [],
      recommendations:
        compositeScore < 60
          ? ['Increase reserve requirements', 'Tighten underwriting thresholds']
          : [],
    };
  }

  // ===== Portfolio Risk =====
  analyzePortfolio(input: PortfolioRiskInput): PortfolioRiskAggregation {
    const sectorValues = Object.values(input.sectorExposures);
    const total = sectorValues.reduce((s, v) => s + v, 0) || 1;
    const diversificationIndex = sectorValues.reduce((s, v) => s + Math.pow(v / total, 2), 0); // Herfindahl index like
    const concentrationAlerts: string[] = [];
    if (diversificationIndex > 0.25) concentrationAlerts.push('Sector concentration risk');
    if (input.averageCoverageRatio < 1.1)
      concentrationAlerts.push('Low average collateral coverage');
    return {
      portfolioId: input.portfolioId,
      diversificationIndex: Number((1 - diversificationIndex).toFixed(3)),
      concentrationAlerts,
      tailRiskEstimate: 0.08 * input.totalExposure,
      stressedLossProjection: 0.12 * input.totalExposure,
      capitalAdequacyFlag: input.weightedAverageRiskScore < 55 ? 'monitor' : 'adequate',
      recommendations: concentrationAlerts.length ? ['Rebalance sector exposures'] : [],
    };
  }

  // ===== Project Viability =====
  assessProjectViability(input: ProjectViabilityInput): ProjectViabilityResult {
    const viabilityScore =
      input.projectedUtilizationRate * 0.3 +
      (100 - input.maintenanceCostRatio * 100) * 0.15 +
      input.communityDemandScore * 0.2 +
      (100 - input.environmentalRiskScore) * 0.15 +
      (100 - input.regulatoryComplexityScore) * 0.2;
    const riskLevel: LoanRiskAssessment['overallRisk'] =
      viabilityScore > 75 ? 'low' : viabilityScore > 55 ? 'medium' : 'high';
    const paybackPeriodMonths = Math.round(
      (input.replacementCost / Math.max(input.projectedUtilizationRate, 1)) * 0.5,
    );
    return {
      projectId: input.projectId,
      viabilityScore,
      riskLevel,
      paybackPeriodMonths,
      netCommunityBenefitScore: Math.round(viabilityScore * 0.9),
      criticalRisks: viabilityScore < 60 ? ['Marginal viability'] : [],
      mitigationStrategies:
        viabilityScore < 60
          ? ['Increase participation threshold', 'Add supplemental collateral']
          : [],
      recommendations: viabilityScore < 60 ? ['Re-scope project phases'] : [],
    };
  }

  // ===== Composite Bundle =====
  buildAdvancedBundle(params: {
    proposalId: string;
    credit: HouseholdCreditAnalysisResult[];
    collateral: CollateralValuationResult;
    community: CommunityRiskProfileResult;
    project: ProjectViabilityResult;
    portfolio?: PortfolioRiskAggregation;
  }): AdvancedProposalRiskBundle {
    // Simple synthesis: average risk proxies into LoanRiskAssessment
    const riskScore =
      ((params.credit.reduce((s, c) => s + c.compositeCreditScore, 0) /
        Math.max(params.credit.length, 1)) *
        0.35 +
        params.collateral.coverageRatioStressed * 50 * 0.25 +
        params.community.compositeScore * 0.2 +
        params.project.viabilityScore * 0.2) /
      1; // already weighted
    const overallRisk: LoanRiskAssessment['overallRisk'] =
      riskScore > 75 ? 'low' : riskScore > 55 ? 'medium' : 'high';
    const synthesized: LoanRiskAssessment = {
      overallRisk,
      riskScore: Math.round(riskScore),
      riskFactors: [],
      mitigatingFactors: [],
      recommendations: [],
      stressTestResults: [],
      regulatoryConsiderations: [],
    };
    return {
      proposalId: params.proposalId,
      householdCredit: params.credit,
      collateralValuation: params.collateral,
      communityRisk: params.community,
      projectViability: params.project,
      portfolioContext: params.portfolio,
      synthesizedAssessment: synthesized,
    };
  }
}

export const bankRiskEvaluationService = new BankRiskEvaluationService();
